import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Grid, CircularProgress, Alert, Card, CardContent, Chip, Divider, InputAdornment, TextField, Button
} from '@mui/material';
import SavingsIcon from '@mui/icons-material/Savings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDecodedToken } from '../utils/useDecodedToken';
import { api } from '../api/client';
import { useNavigate, Navigate } from 'react-router-dom';
import FeatureGuard from '../components/common/FeatureGuard';
import UserTypeIndicator from '../components/common/UserTypeIndicator';
import { refreshUserInfoFromStorage } from '../utils/useDecodedToken';
import { isAuthenticated } from '../utils/auth';

const filterOptions = {
  savings: ['Active', 'Approved', 'Pending', 'Closed', 'Rejected'],
  loan: ['Active', 'Disbursed', 'Pending', 'Overpaid', 'Closed', 'In Arrears', 'Withdrawn'],
  share: ['Active', 'Approved', 'Pending', 'Closed', 'Rejected'],
};

const accountTypeIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'loan': return <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />;
    case 'savings': return <SavingsIcon color="success" sx={{ mr: 1 }} />;
    case 'share': return <AccountBalanceIcon color="secondary" sx={{ mr: 1 }} />;
    default: return <AccountBalanceIcon sx={{ mr: 1 }} />;
  }
};

const statusColor = (status: string | undefined) => {
  switch ((status || '').toLowerCase()) {
    case 'active': return 'success';
    case 'approved': return 'primary';
    case 'pending': return 'info';
    case 'closed': return 'default';
    case 'rejected': return 'error';
    case 'overpaid': return 'warning';
    default: return 'secondary';
  }
};

const Accounts: React.FC = () => {
  const user = useDecodedToken();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [savingsAccounts, setSavingsAccounts] = useState<any[]>([]);
  const [loanAccounts, setLoanAccounts] = useState<any[]>([]);
  const [shareAccounts, setShareAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    // Check authentication before making any API calls
    if (!isAuthenticated()) {

      return;
    }
    
    
    const rawAcc = (user?.accountNumber ?? '') as string;
    const accountNumberValid = !!rawAcc && rawAcc.trim() !== '' && rawAcc.trim().toLowerCase() !== 'undefined' && rawAcc.trim().toLowerCase() !== 'null';
    if (!accountNumberValid) {
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/api/clients/${rawAcc}/accounts`);
      const data = response.data;
      setLoanAccounts(data.loanAccounts || []);
      setSavingsAccounts(data.savingsAccounts || []);
      setShareAccounts(data.shareAccounts || []);
    } catch (err: any) {
      console.error('[ACCOUNTS DEBUG] Error fetching accounts:', err);
      setError('Failed to fetch accounts. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [user?.accountNumber]);

  useEffect(() => {
    // Only fetch accounts if authenticated
    if (isAuthenticated()) {
      fetchAccounts();
    }
  }, [fetchAccounts]);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage && isAuthenticated()) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleTabChange = (_: any, newValue: number) => {
    // Check authentication before tab change
    if (!isAuthenticated()) {

      sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
      window.location.href = '/login';
      return;
    }
    
    setTab(newValue);
    setSelectedFilters([]);
  };

  const handleAccountClick = (accountId: number) => {
    // Check authentication before account click
    if (!isAuthenticated()) {

      sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
      window.location.href = '/login';
      return;
    }
    
    if (tab === 0) {
      navigate(`/loan/${accountId}`);
    } else {
      alert('Navigate to account details for ID: ' + accountId);
    }
  };

  const handleRefresh = async () => {
    // Check authentication before refresh
    if (!isAuthenticated()) {

      sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
      window.location.href = '/login';
      return;
    }
    
    setRefreshing(true);
    setSuccessMessage(null);
    
    try {
      // Clear all account states to force re-fetch
      setLoanAccounts([]);
      setSavingsAccounts([]);
      setShareAccounts([]);
      setError('');
      
      // Force a re-fetch by updating the dependency
      // This will trigger the useEffect to run again
      if (user?.accountNumber) {
        // Trigger immediate re-fetch
        await fetchAccounts();
        setSuccessMessage('Accounts refreshed successfully!');
      }
    } finally {
      setRefreshing(false);
    }
  };

  let accounts = [];
  let filterList: string[] = [];
  let accountType = '';
  if (tab === 0) {
    accounts = loanAccounts;
    filterList = filterOptions.loan;
    accountType = 'loan';
  } else if (tab === 1) {
    accounts = savingsAccounts;
    filterList = filterOptions.savings;
    accountType = 'savings';
  } else {
    accounts = shareAccounts;
    filterList = filterOptions.share;
    accountType = 'share';
  }

  const filteredAccounts = accounts.filter(
    (acc: any) =>
      (!search || (acc.productName || acc.accountNo || '').toLowerCase().includes(search.toLowerCase())) &&
      (selectedFilters.length === 0 || selectedFilters.includes(acc.status?.value || acc.status || ''))
  );

  // If not authenticated, show loading or redirect
  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  return (
    <FeatureGuard feature="accounts">
      <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 1 }}>
            <AccountBalanceIcon sx={{ mr: 1, color: 'primary.main', mb: '-4px' }} /> Accounts
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh user data and accounts"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <UserTypeIndicator />
          </Box>
        </Box>

        {/* Access Level Information */}
        {!user?.accountNumber && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Access Restricted:</strong> You need to verify your SMEDCO account number to view account information. 
              You can still create and manage loan applications from the Loan Applications page.
            </Typography>
          </Alert>
        )}


        {/* Success Message */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center' }}>{accountTypeIcon('loan')}Loan</Box>} />
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center' }}>{accountTypeIcon('savings')}Savings</Box>} />
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center' }}>{accountTypeIcon('share')}Share</Box>} />
      </Tabs>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TextField
          placeholder={`Search ${accountType.charAt(0).toUpperCase() + accountType.slice(1)} Accounts...`}
          value={search}
          onChange={e => {
            // Check authentication before search
            if (!isAuthenticated()) {

              alert('Authentication required. Please log in again.');
              window.location.href = '/login';
              return;
            }
            setSearch(e.target.value);
          }}
          size="small"
          sx={{ mr: 2, width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddCircleOutlineIcon />}
          sx={{ ml: 1, display: tab === 0 || tab === 1 ? 'inline-flex' : 'none' }}
        >
          {tab === 0 ? 'Create Loan Account' : 'Create Savings Account'}
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredAccounts.map((acc: any) => {
            let mappedAccount = acc;
            if (tab === 0) {
              mappedAccount = {
                id: acc.id,
                name: acc.productName || acc.accountNo,
                balance: typeof acc.originalLoan === 'number' ? acc.originalLoan : 0,
                status: acc.status?.value || '',
                accountNo: acc.accountNo,
                productName: acc.productName,
                type: 'loan',
              };
            } else if (tab === 1) {
              mappedAccount = {
                id: acc.id,
                name: acc.productName || acc.accountNo,
                balance: typeof acc.balance === 'number' ? acc.balance : 0,
                status: acc.status?.value || acc.status || '',
                accountNo: acc.accountNo,
                productName: acc.productName,
                type: 'savings',
              };
            } else if (tab === 2) {
              mappedAccount = {
                id: acc.id,
                name: acc.productName || acc.accountNo,
                balance: typeof acc.balance === 'number' ? acc.balance : 0,
                status: acc.status?.value || acc.status || '',
                accountNo: acc.accountNo,
                productName: acc.productName,
                type: 'share',
              };
            }
            return (
              <Grid item xs={12} sm={6} md={4} key={mappedAccount.id}>
                <Card
                  elevation={4}
                  sx={{
                    borderRadius: 3,
                    background: '#f8fafc',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 8, background: '#e3f2fd' },
                  }}
                  onClick={() => handleAccountClick(mappedAccount.id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {accountTypeIcon(mappedAccount.type)}
                      <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                        {mappedAccount.name}
                      </Typography>
                      <Chip
                        label={mappedAccount.status}
                        color={statusColor(mappedAccount.status)}
                        size="small"
                        sx={{ fontWeight: 600, textTransform: 'capitalize', ml: 1 }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Account No: <b>{mappedAccount.accountNo}</b>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Balance: <b>{mappedAccount.balance}</b>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {filteredAccounts.length === 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  {user?.accountNumber ? 'No accounts match your selection' : 'No accounts to show yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {user?.accountNumber
                    ? 'Try clearing the search box or removing filters to see more accounts.'
                    : 'You have not linked a SMEDCO account yet. You can still apply for a loan now or verify your account later to unlock full features.'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Button variant="contained" onClick={() => {
                    // Check authentication before navigation
                    if (!isAuthenticated()) {

                      alert('Authentication required. Please log in again.');
                      window.location.href = '/login';
                      return;
                    }
                    navigate('/loan-application');
                  }}>
                    Go to Loan Applications
                  </Button>
                  {!user?.accountNumber && (
                    <Button variant="outlined" onClick={() => {
                      // Check authentication before navigation
                      if (!isAuthenticated()) {

                        alert('Authentication required. Please log in again.');
                        window.location.href = '/login';
                        return;
                      }
                      navigate('/profile');
                    }}>
                      Verify Account Number
                    </Button>
                  )}
                </Box>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
      </Box>
    </FeatureGuard>
  );
};

export default Accounts; 
